import i18n from 'i18next';
import ChainedBackend from 'i18next-chained-backend';
import type { HttpBackendOptions } from 'i18next-http-backend';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { addPwPrefixToBaseUrl } from 'utils/pwContext';

import { setReqHeaders } from '../../utils';
import { FLOW_ENDPOINTS, NAMESPACE_LIST, WIDGET_API } from '../constants';

type LocizeParams = { brand: string; lang: string };

type PrefetchedTranslations = Record<string, Promise<string | null>>;

interface WindowWithPrefetch extends Window {
  __PREFETCHED_TRANSLATIONS__?: PrefetchedTranslations;
}

type RequestCallback = (err: Error | null, result: { status: number; data: string }) => void;

const isDevelopmentMode = process.env.NODE_ENV === 'development';

const i18nextInitOptions = ({ lang, brand }: LocizeParams) => {
  const lng = lang.toLowerCase() || 'en';
  const { href = '' } = window?.location || {};
  const isWithdrawal = href.includes('withdrawal') && !href.includes('reject-withdrawal');
  const flow = isWithdrawal ? FLOW_ENDPOINTS.withdrawal : FLOW_ENDPOINTS.deposit;
  const brandSuffix = brand ? `-${brand}` : '';

  const baseTranslationPath = `${WIDGET_API}/${flow}/translations/{{ns}}/${lng}${brandSuffix}`;
  const TRANSLATION_LOAD_PATH = addPwPrefixToBaseUrl(baseTranslationPath);

  const prefetched = (window as WindowWithPrefetch).__PREFETCHED_TRANSLATIONS__;

  const fetchWithSession = (url: string, callback: RequestCallback) => {
    const headers = new Headers();
    try {
      setReqHeaders(headers, {
        endpoint: 'i18:requestWithSession',
      });
    } catch (e) {
      console.error(`Error set header with session for translation: ${e}`);
    }
    fetch(url, {
      method: 'GET',
      headers,
      credentials: 'same-origin',
    })
      .then(async (res) => {
        const data = await res.text();
        callback(null, {
          status: res.status,
          data,
        });
      })
      .catch((err) => {
        callback(err, {
          status: 0,
          data: '',
        });
      });
  };

  const requestWithSession: NonNullable<HttpBackendOptions['request']> = async (_options, url, _payload, callback) => {
    // Try to resolve from prefetched data first
    if (prefetched) {
      const matchedNs = NAMESPACE_LIST.find((ns) => url.includes(`/translations/${ns}/`));
      const prefetchedPromise = matchedNs ? prefetched[matchedNs] : undefined;
      if (prefetchedPromise) {
        try {
          const data = await prefetchedPromise;
          if (data) {
            callback(null, {
              status: 200,
              data,
            });
            return;
          }
        } catch (e) {
          console.error('Prefetch failed:', e);
        }
      }
    }

    fetchWithSession(url, callback);
  };

  return {
    debug: false,
    lng,
    ns: NAMESPACE_LIST,
    fallbackNS: NAMESPACE_LIST,
    fallbackLng: false as const,
    keySeparator: false as const,
    saveMissing: false,
    saveMissingPlurals: false,
    version: 'latest',
    interpolation: {
      escapeValue: false,
    },
    ...(isDevelopmentMode
      ? {
          allowedAddOrUpdateHosts: ['localhost'],
        }
      : {}),
    backend: {
      backends: [HttpBackend],
      backendOptions: [
        {
          loadPath: TRANSLATION_LOAD_PATH,
          request: requestWithSession,
        },
      ],
    },
  };
};

export const i18nImportInit = async (params: LocizeParams) => {
  await i18n.use(initReactI18next).use(ChainedBackend).init(i18nextInitOptions(params));

  // Clean up — prefetched promises are consumed, no longer needed
  delete (window as WindowWithPrefetch).__PREFETCHED_TRANSLATIONS__;

  return i18n;
};
